import { useQuery } from '@tanstack/react-query';
import {
  fetchUnidentified,
  fetchUnidentifiedSummary,
  type UnidentifiedPerson,
} from '../../lib/api/unidentified';
import { formatDate, formatNumber, formatRut } from '../../lib/format';
import { useTableState } from '../../hooks/useTableState';
import { DataBoundary } from '../../components/DataBoundary';
import { DataTable, type Column } from '../../components/DataTable';
import { Card, Notice, TableSkeleton } from '../../components/primitives';
import { PageHeading } from '../layout/PageHeading';

/**
 * Marcaciones sin colaborador identificado.
 *
 * ── Qué muestra y por qué merece pantalla propia ───────────────────────────
 * Personas que marcan asistencia en un recinto de OXXEAN y cuyo RUT no corresponde
 * a ningún colaborador de BUK, ni activo ni egresado.
 *
 * No es un residuo: son miles de marcaciones, y algunas personas acumulan cientos en
 * varios meses, que es alguien trabajando de forma regular. Dejarlo como un contador
 * de «omitidos» en el historial de sincronización sería esconder una pregunta que
 * alguien tiene que responder.
 *
 * ── Lo que esta pantalla NO afirma ─────────────────────────────────────────
 * No dice que estas personas sean contratistas, ni que falten en BUK, ni que las
 * marcaciones sobren. Son tres explicaciones posibles con consecuencias distintas, y
 * la plataforma no puede distinguirlas: presentar una como si fuera la respuesta
 * sería inventar.
 *
 * La columna «Empresa del recinto» dice justamente eso —de quién es el torniquete—,
 * no para quién trabaja la persona. Llamarla «Empresa» a secas invitaría a leerla
 * como su empleador, que es el dato que falta.
 */
export function UnidentifiedMarksPage(): JSX.Element {
  const table = useTableState({ pageSize: 25 });

  const resumen = useQuery({
    queryKey: ['unidentified', 'summary'],
    queryFn: fetchUnidentifiedSummary,
  });

  const lista = useQuery({
    queryKey: ['unidentified', 'list', table.state.page, table.state.pageSize, table.state.search],
    queryFn: () =>
      fetchUnidentified({
        page: table.state.page,
        pageSize: table.state.pageSize,
        search: table.state.search,
      }),
  });

  const columns: Array<Column<UnidentifiedPerson>> = [
    {
      key: 'normalizedDni',
      header: 'RUT',
      sortable: false,
      // Se formatea a `27.293.344-8`.
      //
      // Almacenado viaja normalizado —sin puntos ni guion— porque así se compara
      // entre los dos sistemas. Pero esta pantalla existe justamente para que una
      // persona vaya a BUSCAR a estas personas en sus registros, y `272933448` no es
      // un RUT reconocible: obligaría a reconstruirlo mentalmente 95 veces.
      //
      // Si el valor no fuera un RUT válido, `formatRut` devuelve null y se muestra el
      // original: es preferible un dato crudo a ocultar que el origen envió algo
      // que no es un RUT, que sería en sí mismo el hallazgo.
      render: (row) => formatRut(row.normalizedDni) ?? row.normalizedDni,
    },
    {
      key: 'marks',
      header: 'Marcaciones',
      sortable: false,
      align: 'right',
      render: (row) => formatNumber(row.marks, 'integer'),
    },
    {
      key: 'firstMark',
      header: 'Primera marca',
      sortable: false,
      render: (row) => formatDate(row.firstMark),
    },
    {
      key: 'lastMark',
      header: 'Última marca',
      sortable: false,
      render: (row) => formatDate(row.lastMark),
    },
    {
      key: 'venues',
      header: 'Recinto',
      sortable: false,
      render: (row) => row.venues.join(', '),
    },
    {
      // El encabezado dice «del recinto» a propósito: es de quién es el torniquete,
      // NO para quién trabaja la persona. Ese dato es justamente el que falta.
      key: 'companies',
      header: 'Empresa del recinto',
      sortable: false,
      render: (row) => row.companies.join(', '),
    },
  ];

  const s = resumen.data;
  const porcentaje =
    s && s.allMarks > 0 ? ((s.totalMarks / s.allMarks) * 100).toFixed(1) : null;

  return (
    <>
      <PageHeading
        title="Marcaciones sin identificar"
        description={
          'Personas que marcan asistencia en un recinto de OXXEAN y no existen como ' +
          'colaboradores en BUK. Su asistencia queda registrada pero no se atribuye a nadie.'
        }
      />

      <Notice tone="partial">
        <strong>Requiere revisión humana.</strong> La plataforma no puede determinar de
        cuál de estos tres casos se trata, y cada uno tiene una consecuencia distinta:
        <br />
        <br />
        <strong>1.</strong> Son <strong>terceros o contratistas</strong> que marcan en el
        recinto sin estar en la nómina. Es legítimo, y conviene saber cuántos y dónde.
        <br />
        <strong>2.</strong> Es un <strong>colaborador que falta en BUK</strong>, o cuyo RUT
        está escrito distinto en los dos sistemas. Entonces su asistencia no se está
        contando y el indicador de su área está subestimado.
        <br />
        <strong>3.</strong> Es una <strong>marcación que no debería existir.</strong>
        <br />
        <br />
        No se descartan ni se atribuyen a nadie: adivinar por parecido de RUT imputaría
        la asistencia de una persona a otra.
      </Notice>

      {s && (
        <Card title="Resumen">
          <div className="kpi-row">
            <div className="kpi">
              <span className="kpi__label">Personas sin identificar</span>
              <span className="kpi__value">{formatNumber(s.totalPeople, 'integer')}</span>
            </div>
            <div className="kpi">
              <span className="kpi__label">Marcaciones afectadas</span>
              <span className="kpi__value">{formatNumber(s.totalMarks, 'integer')}</span>
              {porcentaje !== null && (
                <span className="kpi__hint">
                  {porcentaje} % de {formatNumber(s.allMarks, 'integer')} marcaciones
                </span>
              )}
            </div>
          </div>

          {s.byCompany.length > 0 && (
            <table className="mini-table">
              <thead>
                <tr>
                  <th>Empresa del recinto</th>
                  <th className="num">Personas</th>
                  <th className="num">Marcaciones</th>
                  <th>Desde</th>
                  <th>Hasta</th>
                </tr>
              </thead>
              <tbody>
                {s.byCompany.map((fila) => (
                  <tr key={fila.company}>
                    <td>{fila.company}</td>
                    <td className="num">{formatNumber(fila.people, 'integer')}</td>
                    <td className="num">{formatNumber(fila.marks, 'integer')}</td>
                    <td>{formatDate(fila.firstMark)}</td>
                    <td>{formatDate(fila.lastMark)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      <Card title="Personas sin identificar">
        <DataBoundary
          isLoading={lista.isLoading}
          isError={lista.isError}
          error={lista.error}
          onRetry={() => void lista.refetch()}
          isEmpty={lista.data?.total === 0}
          empty={{
            title: 'Ninguna marcación sin identificar',
            message:
              'Todas las marcaciones del ámbito corresponden a un colaborador de BUK. ' +
              'Es el resultado deseable, no una falta de datos.',
          }}
          skeleton={<TableSkeleton rows={8} />}
        >
          {lista.data && (
            <DataTable
              caption="Personas que marcan asistencia y no existen en BUK"
              columns={columns}
              // Ordenadas por cantidad de marcaciones desde el SERVIDOR: quien más
              // marca es quien con más probabilidad es un colaborador que falta. Por
              // eso el modo es `server`: reordenar en el cliente solo ordenaría la
              // página visible y rompería ese criterio.
              mode="server"
              rows={lista.data.data}
              getRowId={(row) => row.normalizedDni}
              total={lista.data.total}
              totalPages={lista.data.totalPages}
              state={table.state}
              onStateChange={table.setState}
              searchPlaceholder="Buscar por RUT…"
              isFetching={lista.isFetching}
            />
          )}
        </DataBoundary>
      </Card>
    </>
  );
}
